import { ReportFilters, ReportType } from '@/lib/api/reports/reports-api'
import {
    BILLING_PAYMENT_METHOD_LABELS,
    BILLING_STATUS_LABELS,
    BILLING_STATUS_NONE,
    BILLING_STATUS_NONE_LABEL,
    DATE_BASIS_OPTIONS,
    REPORT_CONFIGS,
} from '../config/report-configs'

/**
 * Traduz os filtros de um relatório para linguagem de operador.
 *
 * A automação guarda os filtros como JSON (`filtersJson`) porque é isso que o cron
 * repassa ao `ReportsService`. Mas **ninguém edita JSON**: o campo existia no modal e
 * era impossível de preencher sem ler o código do backend. Agora os filtros são
 * herdados da tela, e o que o operador vê é este resumo.
 *
 * Chaves sem tradução conhecida são omitidas de propósito: um resumo com
 * `sortDir: desc` só faz o operador duvidar do que está lendo.
 */

/** Filtros que não descrevem recorte de dados — período e paginação. */
const IGNORED_KEYS = new Set([
    'startDate',
    'endDate',
    'page',
    'pageSize',
    'sortBy',
    'sortDir',
    'companyId',
    'organizationId',
])

const ID_LABELS: Record<string, string> = {
    professionalId: 'Profissional',
    clientId: 'Cliente',
    partnerId: 'Parceiro',
    hospitalId: 'Hospital',
    supplierId: 'Fornecedor',
    userId: 'Usuário',
    employeeId: 'Colaborador',
    departmentId: 'Departamento',
    attendantId: 'Atendente',
    unitId: 'Unidade',
    createdById: 'Criado por',
    supplyId: 'Material',
}

const BILLING_KIND_LABELS: Record<string, string> = {
    RECEIVABLE: 'A Receber',
    PAYABLE: 'A Pagar',
}

const ORDER_TYPE_LABELS: Record<string, string> = {
    ORDER: 'Pedido',
    SCHEDULE: 'Agendamento',
}

const toList = (value: unknown): string[] => {
    if (value === undefined || value === null || value === '') return []
    if (Array.isArray(value)) return value.map(String)
    return String(value).split(',').map(v => v.trim()).filter(Boolean)
}

const translate = (values: string[], map: Record<string, string>): string =>
    values.map(v => map[v] ?? v).join(', ')

export interface FilterSummaryEntry {
    label: string
    value: string
}

export function describeReportFilters(
    reportType: ReportType | string,
    filters: ReportFilters | Record<string, any> | null | undefined,
): FilterSummaryEntry[] {
    if (!filters) return []

    const config = REPORT_CONFIGS[reportType as ReportType]
    const out: FilterSummaryEntry[] = []

    const statusValues = toList(filters.status)
    if (statusValues.length) {
        out.push({
            label: 'Status',
            value: translate(statusValues, config?.statusLabels ?? {}),
        })
    }

    const billingStatus = toList(filters.billingStatus)
    if (billingStatus.length) {
        out.push({
            label: 'Status da cobrança',
            value: translate(billingStatus, {
                ...BILLING_STATUS_LABELS,
                [BILLING_STATUS_NONE]: BILLING_STATUS_NONE_LABEL,
            }),
        })
    }

    const billingMethod = toList(filters.billingPaymentMethod)
    if (billingMethod.length) {
        out.push({
            label: 'Meio de pagamento da cobrança',
            value: translate(billingMethod, BILLING_PAYMENT_METHOD_LABELS),
        })
    }

    const paymentMethod = toList(filters.paymentMethod)
    if (paymentMethod.length) {
        out.push({
            label: 'Meio de pagamento',
            value: translate(paymentMethod, BILLING_PAYMENT_METHOD_LABELS),
        })
    }

    const paymentStatus = toList(filters.paymentStatus)
    if (paymentStatus.length) {
        out.push({ label: 'Status do pagamento', value: paymentStatus.join(', ') })
    }

    if (filters.type) {
        const value =
            BILLING_KIND_LABELS[String(filters.type)] ??
            ORDER_TYPE_LABELS[String(filters.type)] ??
            String(filters.type)
        out.push({ label: 'Tipo', value })
    }

    if (filters.dateBasis) {
        const option = DATE_BASIS_OPTIONS?.[reportType as ReportType]?.find(
            (o: { value: string }) => o.value === filters.dateBasis,
        )
        if (option) {
            out.push({ label: 'Período contado por', value: option.label })
        }
    }

    if (filters.scope === 'organization') {
        out.push({ label: 'Abrangência', value: 'Todas as filiais' })
    }

    if (filters.isReturn !== undefined) {
        out.push({ label: 'É retorno', value: filters.isReturn ? 'Sim' : 'Não' })
    }

    // A tela só liga o recorte de cirurgias; `false` (sem cirurgias) ainda pode vir de
    // automação antiga ou link montado à mão, e o backend continua atendendo.
    if (filters.isSurgical !== undefined) {
        out.push({
            label: 'Cirurgia',
            value: filters.isSurgical ? 'Somente cirurgias' : 'Sem cirurgias',
        })
    }

    if (filters.excludeLostSales) {
        out.push({ label: 'Cobranças perdidas', value: 'Ocultas' })
    }

    const catalogIds = toList(filters.catalogIds ?? filters.catalogId)
    if (catalogIds.length) {
        out.push({
            label: 'Itens do catálogo',
            value: `${catalogIds.length} ${catalogIds.length === 1 ? 'item' : 'itens'}`,
        })
    }

    // Ids não têm nome no estado dos filtros (a tela só guarda o id, e o nome vem do
    // seletor). Mostrar "#123" é pior que nada? Não: o operador precisa saber que o
    // recorte existe, senão a automação parece mais ampla do que é.
    for (const [key, label] of Object.entries(ID_LABELS)) {
        const value = (filters as Record<string, any>)[key]
        if (value !== undefined && value !== null && value !== '') {
            out.push({ label, value: `#${value}` })
        }
    }

    for (const key of Object.keys(filters)) {
        if (IGNORED_KEYS.has(key)) continue
    }

    return out
}

/**
 * Filtros que valem a pena gravar na automação: tira período e paginação.
 *
 * O período **não** é herdado de propósito — quem define a janela da automação é o
 * `periodWindow` ("últimos 7 dias", "mês passado"), que é recalculado a cada
 * execução. Herdar as datas da tela congelaria o relatório num intervalo fixo, e o
 * e-mail semanal mandaria sempre os mesmos números.
 */
export function extractAutomationFilters(
    filters: ReportFilters | Record<string, any> | null | undefined,
): Record<string, any> {
    if (!filters) return {}
    const out: Record<string, any> = {}
    for (const [key, value] of Object.entries(filters)) {
        if (IGNORED_KEYS.has(key)) continue
        if (value === undefined || value === null || value === '') continue
        if (Array.isArray(value) && value.length === 0) continue
        out[key] = value
    }
    return out
}
